import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import clsx from "clsx";

// MUI
import { Checkbox, Divider, FormControlLabel, Stack, Typography } from "@mui/material";
import TierAlertError from "@/Layouts/components/Tiers/TierAlertError";

const isAllChecked = (checkBoxes = []) => checkBoxes.every((checkBox) => checkBox?.checked);

const RenderLabel = ({ label = "", classes = "", readOnly = false }) => {
	const defaultClasses = twMerge("!font-sans !text-[14.5px]", classes);

	return (
		<Typography
			className={clsx({
				[defaultClasses]: true,
				"!text-default": !readOnly,
				"!text-disabled": readOnly,
			})}
		>
			{label}
		</Typography>
	);
};
RenderLabel.propTypes = {
	label: PropTypes.string,
	classes: PropTypes.string,
	readOnly: PropTypes.bool,
};

const RenderCheckBox = (props) => {
	const { label = "", checked = false, disabled = false, readOnly = false, onChangeAction = () => {} } = props;
	return (
		<FormControlLabel
			control={
				<Checkbox
					className={clsx({
						"!text-primary": !readOnly && Boolean(checked),
						"[&_svg]:!text-disabled": readOnly,
					})}
					checked={Boolean(checked)}
					disabled={readOnly || disabled}
					onChange={(_, value) => onChangeAction(value)}
				/>
			}
			label={<RenderLabel label={label} readOnly={readOnly} />}
			labelPlacement="end"
		/>
	);
};
RenderCheckBox.propTypes = {
	label: PropTypes.string,
	checked: PropTypes.bool,
	disabled: PropTypes.bool,
	readOnly: PropTypes.bool,
	onChangeAction: PropTypes.func,
};

const getVariableCheckboxes = (args) => {
	const { checkBoxes = [], columns = [], showBy = "label", readOnly = false, handleChangeCheck = () => {} } = args || {};
	const isOrdered = Boolean(columns?.length);

	const getMatches = (options = [], conditions = null) => {
		const { match = "", includes = false } = conditions || {};
		return options.filter((option) => (includes && option?.[showBy]?.includes(match)) || option?.[showBy] === match);
	};

	if (isOrdered) {
		const mappedOptions = checkBoxes.map((checkBox, key) => ({ ...checkBox, originalIndex: key }));
		return (
			<Stack direction={"row"} px={1.4} spacing={3}>
				{columns.map((column, index) => {
					const columnKey = `column_${index}`;
					const options = getMatches(mappedOptions, { ...column });
					return (
						<Stack key={columnKey}>
							{options.map((checkBox, key) => {
								const label = checkBox?.[showBy];
								const originalKey = checkBox?.["originalIndex"];
								return (
									<RenderCheckBox
										key={`${label}_${index}_${originalKey}_${key}`}
										{...checkBox}
										label={label}
										readOnly={readOnly}
										onChangeAction={(value) => handleChangeCheck(originalKey, value)}
									/>
								);
							})}
						</Stack>
					);
				})}
			</Stack>
		);
	}

	return checkBoxes.map((checkBox, key) => {
		const label = checkBox?.[showBy];
		return (
			<RenderCheckBox
				key={`${label}_${key}`}
				{...checkBox}
				label={label}
				readOnly={readOnly}
				onChangeAction={(value) => handleChangeCheck(key, value)}
			/>
		);
	});
};

export default function VariableGrossStructure(props) {
	const {
		grossStructure = [],
		columns = [],
		showBy = "label",
		message = "Invalid Variable Gross Structure",
		error = false,
		readOnly = false,
		showDivider = true,
		showTitle = true,
		onActionUpdate = () => {},
	} = props;

	const [checkBoxes, setCheckBoxes] = useState([]);

	const allChecked = isAllChecked(checkBoxes);
	const isSomeChecked = checkBoxes.some((checkBox) => checkBox?.checked);

	const handleCheckAll = () => {
		let updatedCheckBoxes = [...checkBoxes];
		updatedCheckBoxes = updatedCheckBoxes.map((checkBox) => ({ ...checkBox, checked: !allChecked }));
		setCheckBoxes(updatedCheckBoxes);
		onActionUpdate?.(updatedCheckBoxes);
	};

	const handleChangeCheck = (index, value) => {
		const updatedCheckBoxes = [...checkBoxes];
		updatedCheckBoxes[index]["checked"] = value;
		setCheckBoxes(updatedCheckBoxes);
		onActionUpdate?.(updatedCheckBoxes);
	};

	useEffect(() => {
		setCheckBoxes(grossStructure);
		return () => {
			setCheckBoxes([]);
		};
	}, [grossStructure]);

	return (
		<Stack
			width={1}
			style={{
				paddingTop: "10px",
			}}
		>
			{Boolean(showDivider) && (
				<Stack mt={1.5}>
					<Divider
						className="!border-disabled"
						sx={{
							paddingTop: "10px",
							marginBottom: "16px",
						}}
					/>
				</Stack>
			)}

			{Boolean(showTitle) && (
				<Typography className="!font-montserrat !font-[500] !text-default !text-[20px]">Variable Gross Structure</Typography>
			)}
			<Stack mt={1} ml={-1.3}>
				<Stack direction="row" spacing={2}>
					<FormControlLabel
						control={
							<Checkbox
								className={clsx({
									"!text-primary": !readOnly && (allChecked || isSomeChecked),
									"[&_svg]:!text-disabled": readOnly,
								})}
								checked={allChecked}
								indeterminate={!allChecked && isSomeChecked}
								disabled={readOnly}
								onChange={handleCheckAll}
							/>
						}
						label={<RenderLabel label={"Select All"} readOnly={readOnly} />}
						labelPlacement="end"
					/>
				</Stack>
				<Stack direction="row" spacing={2} mt={1}>
					{getVariableCheckboxes({ checkBoxes, columns, showBy, readOnly, handleChangeCheck })}
				</Stack>
				{error && (
					<Stack mt={1}>
						{" "}
						<TierAlertError text={message} />
					</Stack>
				)}
			</Stack>
		</Stack>
	);
}
VariableGrossStructure.propTypes = {
	grossStructure: PropTypes.array,
	columns: PropTypes.array,
	showBy: PropTypes.string,
	message: PropTypes.string,
	error: PropTypes.bool,
	readOnly: PropTypes.bool,
	showTitle: PropTypes.bool,
	showDivider: PropTypes.bool,
	onActionUpdate: PropTypes.func,
};
